import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogHeader,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog"
import { SquarePen } from "lucide-react"
import type { FlashcardActivity } from "@/services/api/odin/activities/flashcards/study-session/create"
import { useForm } from "react-hook-form"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { updateFlashcardSchema, type UpdateFlashcardSchema } from "../validations/update"
import { zodResolver } from "@hookform/resolvers/zod"
import { Textarea } from "@/components/ui/textarea"
import { useUpdateFlashcard } from "../queries/use-update-flashcard"
import { useParams } from "react-router-dom"
import { useState } from "react"

interface FlashcardEditModalProps {
  flashcard: FlashcardActivity
  handleOnEditFlashcard: (question: string, answer: string) => void
}

export default function FlashcardEditModal({
  flashcard,
  handleOnEditFlashcard,
}: FlashcardEditModalProps) {
  const [isOpen, setIsOpen] = useState(false)
  const { id } = useParams()
  const { mutateAsync: updateFlashcard, isPending } = useUpdateFlashcard()

  const form = useForm<UpdateFlashcardSchema>({
    resolver: zodResolver(updateFlashcardSchema),
    defaultValues: {
      question: flashcard.next.question,
      answer: flashcard.next.answer,
    },
  })

  async function onSubmit(data: UpdateFlashcardSchema) {
    try {
      await updateFlashcard({
        id: id as string,
        index: flashcard.current,
        payload: {
          question: data.question,
          answer: data.answer,
        },
      })
      handleOnEditFlashcard(data.question, data.answer)
      setIsOpen(false)
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="link" size="sm" className="self-end text-primary-500 hover:no-underline">
          <SquarePen />
          Editar
        </Button>
      </DialogTrigger>
      <DialogContent className="px-0">
        <DialogHeader className="flex flex-row items-center justify-between border-b border-neutral-200 pb-4 pl-4">
          <DialogTitle>Editar flashcard</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 px-4">
            <FormField
              control={form.control}
              name="question"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Pregunta</FormLabel>
                  <FormControl>
                    <Textarea disabled={isPending} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="answer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Respuesta</FormLabel>
                  <FormControl>
                    <Textarea disabled={isPending} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="border-t border-neutral-200 pt-4">
              <Button size="sm" className="self-end bg-primary-500" disabled={isPending}>
                Guardar cambios
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
